import React, { ReactElement, useContext, useEffect, useState } from "react";
import { Button, DropdownOption, Input, InputElement, LoadingSpinner } from "../../../common/components";
import { marginBottom, marginRight, media, paddingBottom, paddingRight } from "../../../common/styles";
import { AutoCompleteLocation, useMapAutoComplete } from "./MapAutoComplete/useMapAutoComplete";
import { faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import { AppContext } from "../../../AppDataProvider/AppDataProvider";
import { ErrorConnector } from "../../../common/components/Input/common";
import { emitEvent } from "../../../utils";
import SuggestionsList from "./MapAutoComplete/SuggestionsList";
type Coordinates = {
  latitude: number;
  longitude: number;
};
type Errors = {
  locationText?: string;
};
export type OnFoundLocationOpts = {
  coordinates: Coordinates | null;
  searchStr: string;
};
export type SearchProps = {
  dateDropdownOptions: DropdownOption[];
  onChange?: (date: string, time: string, location: string) => void;
  onLocationError: (hasError: boolean) => void;
  onSearch: (date: string, time: string, location?: OnFoundLocationOpts) => void;
  prefilledOrganisationId?: string;
  selectedCoordinates: Coordinates | null;
  selectedDate: string;
  selectedLocationText: string;
  selectedTime: string;
  timeDropdownOptions: DropdownOption[];
};
export function Search({
  dateDropdownOptions,
  onChange,
  onLocationError,
  onSearch,
  prefilledOrganisationId,
  selectedDate,
  selectedTime,
  selectedCoordinates,
  selectedLocationText,
  timeDropdownOptions
}: SearchProps): ReactElement {
  const {
    t,
    i18n
  } = useTranslation();
  const theme = useTheme();
  const {
    appData
  } = useContext(AppContext);
  const {
    locationSelection,
    mapkitAuthenticationToken
  } = appData.bookingTool;
  const [formErrors, setFormErrors] = useState<Errors>({});
  const [userLocationLoading, setUserLocationLoading] = useState<boolean>(false);
  const [textSearchLoading, setTextSearchLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [coordinates, setCoordinates] = useState<Coordinates | null>(selectedCoordinates);
  const [locationText, setLocationText] = useState<string>(selectedLocationText);
  const [date, setDate] = useState<string>(selectedDate);
  const [time, setTime] = useState<string>(selectedTime);
  const [userLocationCoordinates, setUserLocationCoordinates] = useState<Coordinates | null>(null);

  /**
   * Tracks if any of the input fields have been changed. Used in the scenario
   * where we are have the map (no organisation ID prefilled) so we can control
   * when we need to force the user to hit the "search" button again to update
   * availability.
   */
  const [dirty, setDirty] = useState(false);
  const handleDateChange = (event: React.ChangeEvent<InputElement>) => {
    setDate(event.target.value);
    if (date !== event.target.value) {
      onChange?.(event.target.value, time, locationText);
      setDirty(true);
    }
    if (prefilledOrganisationId) {
      onSearch(event.target.value, time);
    }
  };
  const handleTimeChange = (event: React.ChangeEvent<InputElement>) => {
    setTime(event.target.value);
    if (time !== event.target.value) {
      onChange?.(date, event.target.value, locationText);
      setDirty(true);
    }
    if (prefilledOrganisationId) {
      onSearch(date, event.target.value);
    }
  };
  const clearError = (fieldName: keyof Errors) => {
    if (formErrors[fieldName]) {
      const newErrors = {
        ...formErrors
      };
      delete newErrors[fieldName];
      setFormErrors(newErrors);
    }
  };
  const detectLocation = () => {
    setUserLocationLoading(true);
    navigator.geolocation.getCurrentPosition(position => {
      setUserLocationLoading(false);
      setLocationText("My location");
      setCoordinates({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude
      });
      onLocationError(false);
      onChange?.(date, time, "My location");
    }, error => {
      console.error(error);
      onLocationError(true);
      setUserLocationLoading(false);
    }, {
      enableHighAccuracy: true,
      maximumAge: Infinity,
      timeout: 10000
    });
  };
  const searchLocationDateTime = () => {
    if (!prefilledOrganisationId && !isInputValid()) return;
    setTextSearchLoading(true);
    onLocationError(false);
    const geocoder = new mapkit.Geocoder({
      getsUserLocation: true,
      language: i18n.language
    });
    let foundMappedOverride;
    if (locationSelection) {
      const {
        overrideLocationSearchQueryMapping
      } = locationSelection;
      if (overrideLocationSearchQueryMapping && overrideLocationSearchQueryMapping.length > 0) {
        foundMappedOverride = overrideLocationSearchQueryMapping.find(override => override.from === locationText);
      }
    }
    const includedCountries = locationSelection && locationSelection.includedCountries ? locationSelection.includedCountries : [];

    /**
     * if we have a mapped override value,
     * we want to use that instead of the provided value.
     */
    const lookupValue = foundMappedOverride ? foundMappedOverride.to : locationText;
    const geoCoderOptions = {
      ...(includedCountries.length > 0 && {
        limitToCountries: includedCountries.join(",")
      })
    };
    if (coordinates || prefilledOrganisationId) {
      setLocationText("My location");
      onSearch(date, time, {
        coordinates,
        searchStr: locationText
      });
      setTextSearchLoading(false);
    } else {
      geocoder.lookup(lookupValue, (error, response) => {
        if (error || response.results.length === 0) {
          console.error(error);
          onLocationError(true);
        } else if (response.results.length > 0) {
          onSearch(date, time, {
            coordinates: response.results[0].coordinate,
            searchStr: locationText
          });
          onLocationError(false);
        }
        setTextSearchLoading(false);
      }, geoCoderOptions);
    }
    setDirty(false);
    if (appData.iFrameId) {
      emitEvent({
        data: {
          date: date,
          iFrameId: appData.iFrameId,
          location: lookupValue,
          time: time
        },
        name: "LOCATION_DATE_TIME_SEARCHED"
      });
    }
  };
  const isInputValid = () => {
    const newErrors: Errors = {};
    if (!locationText) newErrors.locationText = t("errors.enterLocation");
    const hasErrors = Object.keys(newErrors).length > 0;
    setFormErrors(newErrors);
    return !hasErrors;
  };
  useEffect(() => {
    detectHintLocation();
    mapkit.init({
      authorizationCallback: done => {
        done(mapkitAuthenticationToken);
        // If the location is prefilled. Perform an immediate search after
        // mapkit has authenticated without requiring the user to click.
        if (locationText || coordinates) {
          searchLocationDateTime();
        }
      },
      language: i18n.language
    });
    // Mapkit initialisation only needs to happen once, additional calls
    // just lead to warnings in the console.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [isLoading, suggestions] = useMapAutoComplete({
    locationHint: userLocationCoordinates,
    searchLocations: locationSelection?.includedCountries?.join(","),
    searchTerm: searchTerm
  });
  const detectHintLocation = () => {
    navigator.geolocation.getCurrentPosition(position => {
      setUserLocationCoordinates({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude
      });
    }, error => {
      console.error(error);
    }, {
      enableHighAccuracy: true,
      maximumAge: Infinity,
      timeout: 10000
    });
  };
  const clickedSuggestion = (item: AutoCompleteLocation) => {
    setLocationText(item.displayLines.join(", "));
    suggestions.length = 0;
  };
  const onCloseDropDown = () => {
    suggestions.length = 0;
  };
  return <Form id="locationDateTimeSearchForm" aria-label={t("locationDateTimeSearchForm")} onSubmit={event => {
    event.preventDefault();
    searchLocationDateTime();
  }} prefilledOrganisationId={Boolean(prefilledOrganisationId)} data-sentry-element="Form" data-sentry-component="Search" data-sentry-source-file="Search.tsx">
      {!prefilledOrganisationId && <LocationContainer>
          <LocationInputContainer>
            <AutoCompleteContainer>
              <LocationInput id={"locationText"} value={locationText} multiple={false} label={t("location")} aria-label={t("locationSearchText")} placeholder={t("locationSearchText")} onChange={(event: React.ChangeEvent<InputElement>) => {
            setLocationText(event.target.value);
            setSearchTerm(event.target.value);
            setCoordinates(null);
            clearError("locationText");
            if (locationText !== event.target.value) {
              onChange?.(event.target.value, time, locationText);
              setDirty(true);
            }
          }} onKeyPress={(event: React.KeyboardEvent) => {
            if (event.key === "Enter") {
              event.preventDefault();
              searchLocationDateTime();
            }
          }} error={formErrors.locationText} />

              {!isLoading && suggestions.length > 0 && <SuggestionsList suggestions={suggestions} onClick={clickedSuggestion} onClose={onCloseDropDown} />}
            </AutoCompleteContainer>
          </LocationInputContainer>
          <UseLocationButton aria-label={t("useCurrentLocation")} onClick={event => {
        event.preventDefault();
        detectLocation();
        setDirty(true);
      }}>
            {userLocationLoading ? <LoadingSpinner colour={theme.colours.dark} /> : <UseLocationIcon icon={faMapMarkerAlt} />}
          </UseLocationButton>
        </LocationContainer>}
      <DateTimeContainer data-sentry-element="DateTimeContainer" data-sentry-source-file="Search.tsx">
        <DateDropdownContainer data-sentry-element="DateDropdownContainer" data-sentry-source-file="Search.tsx">
          <Input variant="dropdown" id={"dateDropdown"} aria-label={t("dateDropdown")} onChange={handleDateChange} label={t("date")} options={dateDropdownOptions} hidePlaceholder selectedOption={selectedDate} data-sentry-element="Input" data-sentry-source-file="Search.tsx" />
        </DateDropdownContainer>
        <TimeDropdownContainer data-sentry-element="TimeDropdownContainer" data-sentry-source-file="Search.tsx">
          <Input variant="dropdown" id={"timeDropdown"} aria-label={t("timeDropdown")} onChange={handleTimeChange} label={t("time")} options={timeDropdownOptions} hidePlaceholder selectedOption={selectedTime} data-sentry-element="Input" data-sentry-source-file="Search.tsx" />
        </TimeDropdownContainer>
      </DateTimeContainer>
      {!prefilledOrganisationId && <SearchButton disabled={!dirty} variant="primary" onClick={event => {
      event.preventDefault();
      searchLocationDateTime();
    }} prefilledOrganisationId={Boolean(prefilledOrganisationId)}>
          {textSearchLoading ? <LoadingSpinner colour={theme.colours.textOnPrimary} /> : t("locationSearchButtonLabel")}
        </SearchButton>}
    </Form>;
}
const AutoCompleteContainer = styled.div`
  position: relative;
  ${marginBottom.m};
`;
const LocationContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
`;
const LocationInputContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  ${marginRight.s};
`;
const LocationInput = styled(Input).attrs({
  variant: "text"
})`
  padding: 7px 12px;
  line-height: 1;
  ${ErrorConnector} {
    margin-bottom: 0;
  }
`;
const Form = styled.form<{
  prefilledOrganisationId: boolean;
}>`
  display: flex;
  flex-direction: ${({
  prefilledOrganisationId
}) => prefilledOrganisationId ? "row" : "column"};
  align-items: flex-end;
  width: 100%;
  ${paddingBottom.m}

  @media ${media.tablet} {
    flex-direction: column;
  }

  align-items: center;
`;
const UseLocationButton = styled(Button).attrs({
  variant: "tertiary"
})`
  display: flex;
  height: 2.2rem;
  width: 2.2rem;
  align-items: center;
  justify-content: center;
`;
const UseLocationIcon = styled(FontAwesomeIcon)`
  width: 1rem;
  height: 1rem;
`;
const DateTimeContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
`;
const DropdownContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 50%;
`;
const DateDropdownContainer = styled(DropdownContainer)`
  ${paddingRight.s}
`;
const TimeDropdownContainer = styled(DropdownContainer)``;
const SearchButton = styled(Button)<{
  prefilledOrganisationId: boolean;
}>`
  width: ${({
  prefilledOrganisationId
}) => prefilledOrganisationId ? "20%" : "100%"};
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: capitalize;

  margin-left: ${({
  prefilledOrganisationId
}) => prefilledOrganisationId ? "16px" : "0"};

  margin-top: ${({
  prefilledOrganisationId
}) => prefilledOrganisationId ? "10px" : "0"};

  @media ${media.tablet} {
    width: 100%;
    margin-left: 0;
    margin-top: 0;
  }
`;